import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CachedService } from 'src/app/core/service/common/cached.service';
import { concatMap, forkJoin, from, map, Observable, toArray } from 'rxjs';
import { Globals } from 'src/app/globals';
import { ActionsService } from 'src/app/core/service/api/actions.service';

@Component({
  selector: 'app-iq-audio-notes-summary',
  templateUrl: './iq-audio-notes-summary.component.html',
  styleUrl: './iq-audio-notes-summary.component.sass',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class IqAudioNotesSummaryComponent implements OnInit {
  selectedContactPerson: any[] = [];
  stakeholders: any[] = [];
  contactpersons: any[] = [];
  users: any[] = [];
  actions: any[] = [];
  showSummary: boolean = false;

  @Input() meeting: any;
  @Output() showAction = new EventEmitter<boolean>();
  @Output() emitMeeting = new EventEmitter<any>();

  constructor(
    private _cachedService: CachedService,
    private _cdRef: ChangeDetectorRef,
    private globals: Globals,
    private actionsService: ActionsService
  ){}

  ngOnInit(): void {
    // Chain both API calls and process data when both are complete
    forkJoin({
      stakeholders: this._cachedService.getStakeholders(),
      contactpersons: this._cachedService.getEmployees(),
      users: this._cachedService.getUsers()
    }).subscribe(({ stakeholders, contactpersons, users }) => {
      this.stakeholders = stakeholders;
      this.contactpersons = contactpersons;
      this.users = users;
      
      // Pre-fill contact persons based on meeting speakers
      this.prefillContactPersonsFromSpeakers();
    });
  }

  prefillContactPersonsFromSpeakers(): void {
    if (!this.meeting?.speakers || !this.contactpersons) {
      console.log('Missing speakers or contactpersons data');
      return;
    }
    
    // Match speakers with contact persons by name
    this.meeting.speakers.forEach((speaker: any) => {
      const matchedContactPerson = this.findMatchingContactPerson(speaker.name);
      
      if (matchedContactPerson) {
        // Add to matched contact persons if not already added
        const existingMatch = this.selectedContactPerson.find(cp => cp.id === matchedContactPerson.id);
        if (!existingMatch) {
          this.selectedContactPerson.push(matchedContactPerson);
        }
        
        // Append contactperson object to the matched speaker
        speaker.contactperson = [matchedContactPerson];
      } else {
        // Ensure no contactperson is set if no match found
        speaker.contactperson = [];
      }
    });
    
      // Pre-fill the selected contact persons
      if (this.selectedContactPerson.length > 0) {
        this.meeting.contactpersons = this.selectedContactPerson;
        
        // Trigger change detection to update UI
        this._cdRef.detectChanges();
        
        // Trigger the onUpdateContactpersons to auto-assign stakeholders
        this.onUpdateContactpersons(this.selectedContactPerson);
      } else {
        console.log('No contact persons matched, skipping pre-fill');
      }
  }

  findMatchingContactPerson(speakerName: string): any | null {
    if (!speakerName || !this.contactpersons) {
      return null;
    }

    // Normalize the speaker name for comparison
    const normalizedSpeakerName = speakerName.toLowerCase().trim();
    
    // Find the best match based on first name or last name
    const match = this.contactpersons.find((contactPerson: any) => {
      const firstName = (contactPerson.first_name || '').toLowerCase().trim();
      const lastName = (contactPerson.last_name || '').toLowerCase().trim();
      const fullName = `${firstName} ${lastName}`.trim();
      
      // More strict matching - only return true for actual meaningful matches
      return this.isValidMatch(normalizedSpeakerName, firstName, lastName, fullName);
    });
    
    return match || null;
  }

  fuzzyMatch(str1: string, str2: string): boolean {
    if (!str1 || !str2) return false;
    
    // Remove extra spaces and normalize
    const s1 = str1.replace(/\s+/g, ' ').trim();
    const s2 = str2.replace(/\s+/g, ' ').trim();
    
    // Check if one string contains the other (case insensitive)
    if (s1.includes(s2) || s2.includes(s1)) {
      return true;
    }
    
    // Split into words and check for word matches
    const words1 = s1.split(' ');
    const words2 = s2.split(' ');
    
    // Check if any word from str1 matches any word from str2
    for (const word1 of words1) {
      for (const word2 of words2) {
        if (word1.length > 2 && word2.length > 2 && 
            (word1.includes(word2) || word2.includes(word1))) {
          return true;
        }
      }
    }
    
    return false;
  }

  isValidMatch(speakerName: string, firstName: string, lastName: string, fullName: string): boolean {
    // Skip if any name is empty or too short
    if (!speakerName || speakerName.length < 2) return false;
    if (!firstName && !lastName) return false;
    
    // Common Dutch name particles to exclude from matching
    const dutchParticles = ['van', 'de', 'der', 'den', 'het', 'op', 'in', 'aan', 'bij', 'over', 'onder', 'voor', 'na', 'te', 'ter', 'ten'];
    
    // Normalize names by removing Dutch particles
    const normalizedSpeakerName = this.removeDutchParticles(speakerName, dutchParticles);
    const normalizedFirstName = this.removeDutchParticles(firstName, dutchParticles);
    const normalizedLastName = this.removeDutchParticles(lastName, dutchParticles);
    const normalizedFullName = `${normalizedFirstName} ${normalizedLastName}`.trim();
    
    // Exact matches (most reliable) - check both original and normalized versions
    if (speakerName === fullName || speakerName === firstName || speakerName === lastName ||
        normalizedSpeakerName === normalizedFullName || normalizedSpeakerName === normalizedFirstName || normalizedSpeakerName === normalizedLastName) {
      return true;
    }
    
    // Only proceed with partial matching if names are substantial enough
    if (normalizedSpeakerName.length < 3) return false;
    
    // Check if normalized speaker name is contained in any of the normalized contact person names
    if (normalizedFullName.includes(normalizedSpeakerName) && normalizedFullName.length > normalizedSpeakerName.length) {
      return true;
    }
    
    // Check if any normalized contact person name is contained in normalized speaker name
    if (normalizedFirstName && normalizedSpeakerName.includes(normalizedFirstName) && normalizedFirstName.length >= 3) {
      return true;
    }
    if (normalizedLastName && normalizedSpeakerName.includes(normalizedLastName) && normalizedLastName.length >= 3) {
      return true;
    }
    
    // Word-based matching for multi-word names (using normalized names)
    const speakerWords = normalizedSpeakerName.split(' ').filter(word => word.length >= 3 && !dutchParticles.includes(word.toLowerCase()));
    const contactWords = [normalizedFirstName, normalizedLastName].filter(word => word && word.length >= 3 && !dutchParticles.includes(word.toLowerCase()));
    
    // Check if any speaker word matches any contact word
    for (const speakerWord of speakerWords) {
      for (const contactWord of contactWords) {
        if (speakerWord === contactWord || 
            (speakerWord.includes(contactWord) && contactWord.length >= 3) ||
            (contactWord.includes(speakerWord) && speakerWord.length >= 3)) {
          return true;
        }
      }
    }
    
    return false;
  }

  removeDutchParticles(name: string, particles: string[]): string {
    if (!name) return '';
    
    const words = name.toLowerCase().split(' ');
    const filteredWords = words.filter(word => !particles.includes(word));
    
    return filteredWords.join(' ').trim();
  }

  onUpdateContactpersons(event: any) {
    // Extract stakeholders from selected contact persons
    const selectedStakeholders: any[] = [];
    this.selectedContactPerson = [];
    if (this.meeting.contactpersons && this.meeting.contactpersons.length > 0) {
      this.meeting.contactpersons.forEach((contactPerson: any) => {
        this.selectedContactPerson.push(contactPerson);
        if (contactPerson.stakeholders && contactPerson.stakeholders.length > 0) {
          contactPerson.stakeholders.forEach((stakeholder: any) => {
            // Check if stakeholder is not already in the selectedStakeholders array
            const existingStakeholder = selectedStakeholders.find(s => s.id === stakeholder.id);
            if (!existingStakeholder) {
              selectedStakeholders.push(stakeholder);
            }
          });
        }
      });
    }
    
    // Update the meeting stakeholders with the extracted stakeholders
    this.meeting.stakeholders = selectedStakeholders;
    
    // Trigger change detection to update UI
    this._cdRef.detectChanges();
  }

  showSummaries() {
    this.showSummary = true;
    this.showAction.emit(true);
  }

  approveSuggestedTask(task, taskIndex) {
    const addTask = {
      description: task.advice || '',
      topic_index: task.topic_index
    }
    this.meeting.topics[task.topic_index].tasks.push(addTask);
    this.meeting.topics[task.topic_index].suggestedTasks.splice(taskIndex, 1)
    this._cdRef.detectChanges();
  }

  changeDeadline() {
    this.emitMeeting.emit(this.meeting);
  }


  getContent(): Observable<any> {
    const actions = this.meeting.tasks.map(task => ({
      text: task.description,
      timestamp: task.deadline ?? new Date(),
      handled: false,
      stakeholders: this.stakeholders.filter(stk => stk.name === task.owner) || [],
      users: this.users.filter(user => user.id === this.globals.user.id),
      contactmoments: [],
      direct_mail: false
    }));
  
    return from(actions).pipe(
      concatMap(action => this.actionsService.submitAction(action)), // sequential
      toArray(), // wait until all finished
      map(submittedActions => ({
        users_id: this.globals.user?.id,
        title: this.meeting.title,
        contact_moment_timestamp: this.meeting?.meetingDate,
        contact_moment_type: 'meeting',
        stakeholders: this.meeting.stakeholders,
        contactpersons: this.meeting.contactpersons,
        description: this.generateHtml(),
        tasks: submittedActions,
      }))
    );
  }

  generateHtml() {
    let description = '';
    description += `<p>${this.meeting.summary}</p>\n`
    this.meeting.speakers.forEach(speaker => {
      description += '<p></p>\n';
      const speakerName = (speaker.contactperson && speaker.contactperson.length > 0) ? (speaker.contactperson[0].first_name + ' ' + speaker.contactperson[0].last_name).trim() : speaker.name;
      if (speakerName)
        description += `<p><strong>${speakerName}</strong></p>`
      if (speaker.summary)
        description += `<p>${speaker.summary}</p>\n`
    })

    this.meeting.topics.forEach(topic => {
      if (topic.name || topic.summary) {
        description += '<p></p>\n'
        if (topic.name)
          description += `<p><strong>${topic.name}</strong></p>`
        if (topic.summary)
          description += `<p>${topic.summary}</p>\n`
      }
    })

    return description;

  }
  
}
